import React from 'react';
import { Button, Drawer, Table } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import useApiClient from '../../../../hooks/api/useApiClient';
import { useGetCustomerSuccessStatsQuery } from '../../../../store/api-slices';
import { downloadTableAsCsv } from '../../../../services/helpers/TableExportHelpers';

function DrawerDetails({ metric, month, onClose, ...props }) {
  const { t } = useTranslation(null, { keyPrefix: 'Views:Dashboard:ServiceProvider:CustomerSuccessTeam.details' });
  const { t: tCompanyTier } = useTranslation(null, { keyPrefix: 'Shared:CompanyTiers' });
  const { StatsManager } = useApiClient();
  const formattedMonth = month.format('YYYY-MM-DD');
  const { data, isLoading } = useGetCustomerSuccessStatsQuery(
    {
      apiManager: StatsManager,
      month: formattedMonth,
      metric,
    },
    { skip: !metric || !month },
  );
  const formatLiveFrom = (item) => (item?.live_from ? dayjs(item.live_from).format('YYYY-MM-DD') : '-');
  const columns = [
    {
      title: t('name'),
      key: 'name',
      dataIndex: 'name',
    },
    {
      title: t('owner'),
      key: 'owner',
      dataIndex: 'owner',
    },
    {
      title: t('owner_phone'),
      key: 'owner_phone',
      dataIndex: 'owner_phone',
    },
    {
      title: t('owner_email'),
      key: 'owner_email',
      dataIndex: 'owner_email',
    },
    {
      title: t('live_from'),
      key: 'live_from',
      render: formatLiveFrom,
      csvRender: formatLiveFrom,
    },
    {
      title: t('tier', { month: moment(data?.prev_period_month).format('MMMM') }),
      key: 'prev_tier',
      render: (item) => tCompanyTier(item.prev_tier),
      csvRender: (item) => tCompanyTier(item.prev_tier),
    },
    {
      title: t('tier', { month: moment(data?.current_period_month).format('MMMM') }),
      key: 'tier',
      render: (item) => tCompanyTier(item.tier),
      csvRender: (item) => tCompanyTier(item.tier),
    },
    {
      title: t('sales', { month: moment(data?.prev_period_month).format('MMMM') }),
      key: 'prev_sales',
      dataIndex: 'prev_sales',
    },
    {
      title: t('sales', { month: moment(data?.current_period_month).format('MMMM') }),
      key: 'sales',
      dataIndex: 'sales',
    },
    {
      title: t('best_sales'),
      key: 'best_sales',
      dataIndex: 'best_sales',
    },
    {
      title: t('subscriptions'),
      key: 'subscriptions',
      dataIndex: 'subscriptions',
    },
    {
      title: t('address'),
      key: 'address',
      dataIndex: 'address',
    },
  ];

  return (
    <Drawer
      size="70dvw"
      {...props}
      open={!!metric}
      onClose={onClose}
      title={
        <div className="flex justify-end">
          <Button
            icon={<ExportOutlined />}
            disabled={!data?.data?.length}
            onClick={() =>
              downloadTableAsCsv({
                columns,
                dataSource: data?.data,
                filename: `customer-success-${metric}-${formattedMonth}.csv`,
              })
            }
          >
            {t('export_csv')}
          </Button>
        </div>
      }
    >
      <Table loading={isLoading} dataSource={data?.data} columns={columns} />
    </Drawer>
  );
}

export default DrawerDetails;
